/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE REVIEW'S MEDIA (W82 step 5) — PURE; `reviewMedia.test.ts` runs every rule here.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * His §8/§11 (2026-09-26): every exercise card of the review carries ONE row of three containers — Comment · Voice ·
 * Video — ONE of each per exercise, so the row never grows; the overall review's card carries Voice · Video · Live review.
 * A recording's button becomes `● 0:14 · Stop` IN PLACE; a filled container reads `▶ 0:41` with an × at its top-left.
 *
 * THE MODEL (migration `20260926120000`): a voice rides `coach_messages.audio_path` (`<sender>/<id>.m4a`), a video
 * `video_path` (`<sender>/<id>.mp4`); both objects live in the private bucket `coach-review-media`, where each party
 * uploads into their OWN folder and removes their OWN, and only the two parties read. A removed object leaves the row's
 * path standing: the players read the missing object as "Removed".
 *
 * ⚠️ WHERE A ROW'S VOICE LIVES IS ONE RULE (`mediaHome`, the phone's `ReviewMedia.home` its twin, Fable 2026-09-26): a
 * review, a comment, or a reply whose chain ROOTS at a comment keeps its voice in `coach-review-media`; every other
 * message (the Inbox's own voice notes) keeps it in `coach-voice`, which nobody may delete from. Video is always review
 * media. The bucket NAMES are the callers' (`inboxSurfaces.ts` is the one web file allowed to spell `coach-voice`).
 *
 * ⚠️ UPLOADS HAPPEN AT SEND, NEVER WHILE DRAFTING (the brief's own bound, confirmed by Fable 2026-09-26): the brief also
 * said "uploads it while the next records"; the undo window must cost nothing, so a stopped recording lands in memory
 * (the container shows `▶ 0:41` at once) and ONE ordered queue uploads every take after the 10-second undo, then the
 * rows are inserted — and if the insert fails, the uploaded objects are removed again (the owner's delete).
 */
import { rootOf, type FoldRow } from "./reviewBody";
import { voiceCeilingSeconds, voiceClock, voiceNoteBody } from "./messages";

/** The private bucket for every review take (migration `20260926120000`). */
export const REVIEW_MEDIA_BUCKET = "coach-review-media";

export type MediaKind = "voice" | "video" | "photo";
export type MediaHome = "review" | "inbox";

/** Which bucket holds this row's `kind` of media: `review` (coach-review-media) or `inbox` (coach-voice). */
export function mediaHome<T extends FoldRow>(row: T, kind: MediaKind, byId: ReadonlyMap<string, T>): MediaHome {
  if (kind !== "voice") return "review";
  if (row.kind === "review" || row.kind === "comment") return "review";
  return rootOf(row, byId).kind === "comment" ? "review" : "inbox";
}

/** The object's name — `<sender>/<message id>.m4a|mp4`, LOWERCASE (Postgres renders a uuid lowercase and every owner
 *  folder policy compares text; the 2026-09-12 lesson). The SAME string the two path CHECKs hold. */
export function mediaPath(senderId: string, messageId: string, kind: MediaKind): string {
  return `${senderId.toLowerCase()}/${messageId.toLowerCase()}.${MEDIA_EXT[kind]}`;
}

export const mediaContentType: Record<MediaKind, string> = { voice: "audio/mp4", video: "video/mp4", photo: "image/jpeg" };
/** Each kind's extension — the three path CHECKs' exact shape (`coach_messages_{audio,video,photo}_path_shape`). */
export const MEDIA_EXT: Record<MediaKind, string> = { voice: "m4a", video: "mp4", photo: "jpg" };

/* ── the ceilings — each recorder stops itself, so the database never has to refuse a take ─────────────────────────── */

/** A comment's round video. */
export const COMMENT_VIDEO_SECONDS = 60;
/** The overall review's round video. */
export const OVERALL_VIDEO_SECONDS = 180;
/** The live review (the screen + the face). ⚠️ ALSO the SQL ceiling: `coach_messages_video_seconds between 1 and 600`. */
export const LIVE_REVIEW_SECONDS = 600;
export const VIDEO_CHECK_MAX_SECONDS = 600;

/** The key of a recording slot: `overall`, an exercise row's id, or `reply:<comment id>`. */
export type SlotKey = string;
export const OVERALL: SlotKey = "overall";
export const replySlot = (commentId: string): SlotKey => `reply:${commentId}`;

export type Mode = "voice" | "video" | "live";
export function ceilingFor(slot: SlotKey, mode: Mode): number {
  if (mode === "voice") return voiceCeilingSeconds;
  if (mode === "live") return LIVE_REVIEW_SECONDS;
  return slot === OVERALL ? OVERALL_VIDEO_SECONDS : COMMENT_VIDEO_SECONDS;
}
/** A take's length as stored: whole seconds, at least 1, never past the ceiling. */
export function takeSeconds(elapsedMs: number, ceiling: number): number {
  const s = Number.isFinite(elapsedMs) ? Math.round(elapsedMs / 1000) : 1;
  return Math.min(ceiling, Math.max(1, s));
}

/* ── THE ONE RECORDER — a state machine; the page holds the blobs, this holds the rule ─────────────────────────────── */

/** A photo's take has `seconds: 0` (it is picked, not recorded) and lands by `finish` alone — it never `start`s. */
export type Take = { readonly seconds: number; readonly ref: string; readonly live: boolean };
export type Slot = { readonly voice?: Take; readonly video?: Take; readonly photo?: Take };
export type Active = { readonly slot: SlotKey; readonly kind: MediaKind; readonly live: boolean; readonly startedAtMs: number };
export type RecState = { readonly active: Active | null; readonly slots: Readonly<Record<SlotKey, Slot>> };
export const emptyRec: RecState = { active: null, slots: {} };

export type RecEvent =
  | { readonly type: "start"; readonly slot: SlotKey; readonly kind: MediaKind; readonly live?: boolean; readonly atMs: number }
  | { readonly type: "stop" }
  | { readonly type: "finish"; readonly slot: SlotKey; readonly kind: MediaKind; readonly take: Take }
  | { readonly type: "remove"; readonly slot: SlotKey; readonly kind: MediaKind };

export type RecStep = {
  readonly state: RecState;
  /** The recording the page must STOP now (its take arrives later as `finish`) — the stop-on-next rule. */
  readonly stop: Active | null;
  /** Why a start was refused (the slot already holds one of that kind — his "one of each"). */
  readonly refused: string | null;
};

export function recorder(state: RecState, ev: RecEvent): RecStep {
  switch (ev.type) {
    case "start": {
      const held = state.slots[ev.slot]?.[ev.kind];
      if (held !== undefined) {
        const where = ev.slot === OVERALL ? "on the review" : "per exercise";
        return { state, stop: null, refused: ev.kind === "voice" ? `One voice note ${where} — remove it to record again.` : `One video ${where} — remove it to record again.` };
      }
      if (state.active !== null && state.active.slot === ev.slot && state.active.kind === ev.kind) {
        // the same button again = Stop
        return { state: { ...state, active: null }, stop: state.active, refused: null };
      }
      return { state: { ...state, active: { slot: ev.slot, kind: ev.kind, live: ev.live === true, startedAtMs: ev.atMs } }, stop: state.active, refused: null };
    }
    case "stop":
      return { state: { ...state, active: null }, stop: state.active, refused: null };
    case "finish": {
      const cur = state.slots[ev.slot] ?? {};
      return { state: { ...state, slots: { ...state.slots, [ev.slot]: { ...cur, [ev.kind]: ev.take } } }, stop: null, refused: null };
    }
    case "remove": {
      const cur = state.slots[ev.slot] ?? {};
      const next: { voice?: Take; video?: Take; photo?: Take } = { ...cur };
      delete next[ev.kind];
      return { state: { ...state, slots: { ...state.slots, [ev.slot]: next } }, stop: null, refused: null };
    }
  }
}

/* ── what a container says ─────────────────────────────────────────────────────────────────────────────────────── */

export type ContainerState = "empty" | "recording" | "uploading" | "filled" | "removed" | "failed";
export type UploadStatus = "uploading" | "done" | "failed";

export function containerState(p: { filled: boolean; recording: boolean; upload?: UploadStatus | null; removed?: boolean }): ContainerState {
  if (p.recording) return "recording";
  if (p.upload === "uploading") return "uploading";
  if (p.upload === "failed") return "failed";
  if (p.removed === true) return "removed";
  return p.filled ? "filled" : "empty";
}

const EMPTY_WORD: Record<Mode, string> = { voice: "Voice", video: "Video", live: "Live review" };

/** The container's words: `Voice` · `● 0:14 · Stop` · `Uploading…` · `▶ 0:41` · `Removed` · `Not uploaded`. */
export function containerLabel(mode: Mode, state: ContainerState, seconds: number, elapsedSeconds = 0): string {
  switch (state) {
    case "empty": return EMPTY_WORD[mode];
    case "recording": return `● ${voiceClock(elapsedSeconds)} · Stop`;
    case "uploading": return "Uploading…";
    case "failed": return "Not uploaded";
    case "removed": return "Removed";
    case "filled": return `▶ ${voiceClock(seconds)}`;
  }
}

/** The Photo icon's click (Fable 2026-10-07, FIX 2 — ONE of each): a held photo OPENS it, never the picker (the × first,
 *  then pick again); only an empty slot picks; while it uploads or after it failed, nothing. */
export function photoIconClick(state: ContainerState): "open" | "pick" | null {
  if (state === "filled") return "open";
  if (state === "empty") return "pick";
  return null;
}

/** THE COMPOSER'S + MENU (job 3 step 5 page 2 — the web takes his 10-07 composer, the phone's `ReviewComposer.plusMenu`):
 *  the phone's words in the phone's order — `Video` · `Live review` (the OVERALL only) · `Photos`. The phone's `Camera`
 *  (take a still) is NOT drawn: MEASURED, the web's camera is wired for VIDEO alone (`RoundVideo.tsx` records
 *  `getUserMedia`); `Photos` is the file picker, the web's photo library. ONE of each, as the phone: Video and Live review
 *  share the review's ONE video (either held, both wait — the × first); Photos waits while a photo is held. */
export type ComposerMode = "video" | "live" | "photos";
export type ComposerItem = { readonly mode: ComposerMode; readonly word: "Video" | "Live review" | "Photos"; readonly disabled: boolean };
export function composerMenu(slot: Slot | undefined, p: { live: boolean; videoEngine: boolean }): ComposerItem[] {
  const video = slot?.video !== undefined || !p.videoEngine;
  return [
    { mode: "video", word: "Video", disabled: video },
    ...(p.live ? [{ mode: "live" as const, word: "Live review" as const, disabled: video }] : []),
    { mode: "photos", word: "Photos", disabled: slot?.photo !== undefined },
  ];
}
/** THE MIC/SEND SWITCH (W84-4, the phone's `showsMic`; the web thread's since job 3 step 5 page 5): what the composer draws
 *  at the field's end. The review (no `send`) always draws the mic. The thread: a recording under way → the mic (its ■);
 *  nothing to send (no words, no take held) and a mic this browser can record with → the mic; otherwise Send — with no
 *  mic Send stays, dimmed while there is nothing to send (the composer's `disabled`). `held` = ANY take waits in the field —
 *  a voice note, a video or a photo (job 3 step 6 page 2: the review's reply sends with this switch too, and a photo alone
 *  is a reply; the Inbox holds only a voice note, so there it is the voice). */
export function composerAction(p: { send: boolean; mic: boolean; canRecord: boolean; recording: boolean; text: string; held: boolean }): "mic" | "send" | "none" {
  if (!p.send) return p.mic ? "mic" : "none";
  const nothing = p.text.trim() === "" && !p.held;
  return p.mic && p.canRecord && (p.recording || nothing) ? "mic" : "send";
}
/** The mic inside the field: a TAP starts and ■ keeps; a HOLD this long records while held and keeps it on release (the
 *  phone's `onLongPressGesture(minimumDuration: 0.3)` — HIG: a gesture is never the only way). */
export const MIC_HOLD_MS = 300;

/* ── the missing object ────────────────────────────────────────────────────────────────────────────────────────── */

/** Does a storage error mean "this object is not there any more"? (`createSignedUrl` on a removed object answers
 *  `Object not found`.) Anything else is a failed fetch, never a removal. */
export function isMissingObject(error: { message?: string; statusCode?: string | number } | null | undefined): boolean {
  if (error == null) return false;
  return /not[ _-]?found/i.test(String(error.message ?? "")) || String(error.statusCode ?? "") === "404";
}

/** The line a removed attachment leaves. On the COACH's web page: his own → `You removed this`; the client's → `Removed
 *  by your client`. On the client's phone the twin says `Removed by your coach`. */
export function removedLine(viewerIsSender: boolean, senderIsCoach: boolean): string {
  if (viewerIsSender) return "You removed this";
  return senderIsCoach ? "Removed by your coach" : "Removed by your client";
}

/* ── the rows Send inserts, with their media ──────────────────────────────────────────────────────────────────── */

export type MediaFields = { audio_path?: string; audio_seconds?: number; video_path?: string; video_seconds?: number; photo_path?: string };

/** The columns one row carries for its takes — each pair together (the CHECKs `coach_messages_{audio,video}_pair`). */
export function mediaFields(senderId: string, messageId: string, slot: Slot | undefined): MediaFields {
  const out: MediaFields = {};
  if (slot?.voice !== undefined) { out.audio_path = mediaPath(senderId, messageId, "voice"); out.audio_seconds = slot.voice.seconds; }
  if (slot?.video !== undefined) {
    out.video_path = mediaPath(senderId, messageId, "video");
    out.video_seconds = Math.min(VIDEO_CHECK_MAX_SECONDS, slot.video.seconds);
  }
  if (slot?.photo !== undefined) out.photo_path = mediaPath(senderId, messageId, "photo");
  return out;
}

/** One upload in the queue: the take's ref (the page resolves it to its Blob), where it goes, and the slot it fills. */
export type UploadItem = { readonly slot: SlotKey; readonly kind: MediaKind; readonly ref: string; readonly path: string; readonly contentType: string };

/** The queue, in the order the containers are drawn (the overall first, then the exercises in page order). */
export function uploadItems(senderId: string, rows: readonly { slot: SlotKey; id: string }[], slots: Readonly<Record<SlotKey, Slot>>): UploadItem[] {
  const out: UploadItem[] = [];
  for (const r of rows) {
    for (const kind of ["voice", "video", "photo"] as const) {
      const take = slots[r.slot]?.[kind];
      if (take !== undefined) out.push({ slot: r.slot, kind, ref: take.ref, path: mediaPath(senderId, r.id, kind), contentType: mediaContentType[kind] });
    }
  }
  return out;
}

/** Does this slot hold any take? A comment with no words but a video IS a comment. */
export function hasTake(slot: Slot | undefined): boolean {
  return slot?.voice !== undefined || slot?.video !== undefined || slot?.photo !== undefined;
}

/* ── the video recorder's container — MP4/H.264 or nothing ─────────────────────────────────────────────────────── */

/** ⚠️ H.264 IN MP4 OR NOTHING: the phone's AVPlayer cannot play WebM/VP8, and the CHECK refuses any path but `.mp4`.
 *  Firefox records video only as WebM, so it gets NO record button and the sentence naming it. */
export const videoMimeCandidates = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/mp4",
] as const;
export function videoRecorderMime(isTypeSupported: (mime: string) => boolean): string | null {
  for (const m of videoMimeCandidates) if (isTypeSupported(m)) return m;
  return null;
}
export const VIDEO_UNSUPPORTED = "Not on this browser — Firefox records video only in a format the phone cannot play. Safari or Chrome can.";

/** Bitrates chosen so the longest take fits the bucket's 100 MB with room: a 60 s round comment ≈ 6 MB, the 10-minute
 *  live review ≈ 80 MB (1 Mbps video + 64 kbps audio). */
export const VIDEO_BITS: Record<"round" | "live", number> = { round: 800_000, live: 1_000_000 };
export const AUDIO_BITS = 64_000;
export const BUCKET_LIMIT_BYTES = 104_857_600;

/** Does a video read as the live review (wide) or a round self-video (square)? Read from the file itself, so no column. */
export function videoShape(width: number, height: number): "round" | "wide" {
  if (!(width > 0) || !(height > 0)) return "round";
  return width / height > 1.15 ? "wide" : "round";
}

/* ── a reply's words ────────────────────────────────────────────────────────────────────────────────────────── */

/** What a video says to every reader that knows only `body` (the voice note's `🎤 Voice note` precedent). */
export const videoBody = "🎥 Video";
/** What a photo says to every reader that knows only `body`. */
export const photoBody = "📷 Photo";

/** 🔴 HIS 10-10 ASK (2) — WHAT ONE CHAT SEND WRITES, in order (the phone's `ChatSendPlan`, the same cases; the harness gate
 *  `chat-media` executes that one): a held voice note → its OWN row first (`coach-voice`, W72 step 6's path, untouched); a held
 *  photo and/or round video → ONE row carrying both, the words as its caption (no words → what the take is, `replyBody`);
 *  otherwise the words alone. Nothing held and no words → nothing. */
export type ChatStep = { readonly kind: "voice" } | { readonly kind: "media"; readonly body: string } | { readonly kind: "words"; readonly body: string };
export function chatSendPlan(text: string, slot: Slot | undefined): ChatStep[] {
  const t = text.trim();
  const out: ChatStep[] = [];
  if (slot?.voice !== undefined) out.push({ kind: "voice" });
  // `replyBody` asks video, then photo, before voice — so a held voice note never names this row.
  if (slot?.video !== undefined || slot?.photo !== undefined) out.push({ kind: "media", body: replyBody(t, slot) });
  else if (t !== "") out.push({ kind: "words", body: t });
  return out;
}

/** The words under a chat message's photo / video (the phone's `ChatMedia.caption`): its caption, or null when the body only
 *  says what the take is. */
export function chatCaption(body: string): string | null {
  const b = body.trim();
  return b === "" || b === photoBody || b === videoBody ? null : b;
}

/** A reply's body: its words, or — a take and no words — what the take is, so no row is ever blank. */
export function replyBody(text: string, slot: Slot | undefined): string {
  const t = text.trim();
  if (t !== "") return t;
  if (slot?.video !== undefined) return videoBody;
  if (slot?.photo !== undefined) return photoBody;
  if (slot?.voice !== undefined) return voiceNoteBody;
  return "";
}
